/*
  Viyo Organic custom elements.
  Written as web components in the same style as Dawn's own global.js so the
  theme editor can re-render sections without leaking listeners.
*/

if (!customElements.get('viyo-tabs')) {
  customElements.define(
    'viyo-tabs',
    class ViyoTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

        if (this.tabs.length === 0) return;

        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.select(index));
          tab.addEventListener('keydown', (event) => this.onKeyDown(event, index));
        });
      }

      select(index) {
        this.tabs.forEach((tab, i) => {
          const selected = i === index;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.setAttribute('tabindex', selected ? '0' : '-1');
        });

        this.panels.forEach((panel, i) => {
          panel.hidden = i !== index;
        });
      }

      onKeyDown(event, index) {
        let next = null;

        if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;

        if (next === null) return;

        event.preventDefault();
        this.select(next);
        this.tabs[next].focus();
      }
    }
  );
}

if (!customElements.get('viyo-reel')) {
  customElements.define(
    'viyo-reel',
    class ViyoReel extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        this.playButton = this.querySelector('.viyo-reel__play');

        if (!this.video) return;

        this.video.setAttribute('playsinline', '');
        this.addEventListener('click', this.toggle.bind(this));

        // Pause when scrolled out of view so several reels never play at once.
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (!entry.isIntersecting) this.pause();
              });
            },
            { threshold: 0.4 }
          );
          this.observer.observe(this);
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      toggle(event) {
        event.preventDefault();
        if (this.video.paused) {
          // Stop any other reel that is currently playing.
          document.querySelectorAll('viyo-reel').forEach((reel) => {
            if (reel !== this && typeof reel.pause === 'function') reel.pause();
          });
          this.video.play();
          if (this.playButton) this.playButton.hidden = true;
        } else {
          this.pause();
        }
      }

      pause() {
        if (!this.video || this.video.paused) return;
        this.video.pause();
        if (this.playButton) this.playButton.hidden = false;
      }
    }
  );
}

if (!customElements.get('viyo-sticky-atc')) {
  customElements.define(
    'viyo-sticky-atc',
    class ViyoStickyAtc extends HTMLElement {
      connectedCallback() {
        // Watch the real add-to-cart button; show the sticky bar only once the
        // shopper has scrolled past it.
        this.trigger = document.querySelector('product-form .product-form__submit') || document.querySelector('.product-form__submit');

        if (!this.trigger || !('IntersectionObserver' in window)) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
            });
          },
          { rootMargin: '0px 0px -100% 0px' }
        );

        this.observer.observe(this.trigger);

        // The sticky button just forwards to the real form so variant state,
        // discounts and cart drawer behaviour all stay in one place.
        const button = this.querySelector('[data-viyo-sticky-submit]');
        if (button) {
          button.addEventListener('click', () => this.trigger.click());
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }
    }
  );
}

if (!customElements.get('viyo-carousel')) {
  customElements.define(
    'viyo-carousel',
    class ViyoCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-viyo-carousel-track]');
        this.prev = this.querySelector('[data-viyo-carousel-prev]');
        this.next = this.querySelector('[data-viyo-carousel-next]');

        if (!this.track) return;

        // Scroll by one card width so the snap points always line up.
        this.onPrev = () => this.scrollByCard(-1);
        this.onNext = () => this.scrollByCard(1);
        this.onScroll = this.updateButtons.bind(this);

        if (this.prev) this.prev.addEventListener('click', this.onPrev);
        if (this.next) this.next.addEventListener('click', this.onNext);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });

        this.updateButtons();
      }

      disconnectedCallback() {
        if (this.prev) this.prev.removeEventListener('click', this.onPrev);
        if (this.next) this.next.removeEventListener('click', this.onNext);
        if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      }

      scrollByCard(direction) {
        const card = this.track.querySelector('li');
        if (!card) return;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        this.track.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: 'smooth' });
      }

      updateButtons() {
        const max = this.track.scrollWidth - this.track.clientWidth - 1;
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 0;
        if (this.next) this.next.disabled = this.track.scrollLeft >= max;
      }
    }
  );
}

if (!customElements.get('viyo-anchor-nav')) {
  customElements.define(
    'viyo-anchor-nav',
    class ViyoAnchorNav extends HTMLElement {
      connectedCallback() {
        this.links = Array.from(this.querySelectorAll('[data-viyo-anchor-link]'));
        if (this.links.length === 0) return;

        // Offset scrolling by the sticky header plus this bar so a jumped-to
        // heading is not hidden underneath them.
        this.links.forEach((link) => {
          link.addEventListener('click', (event) => {
            const target = document.getElementById(link.getAttribute('href').slice(1));
            if (!target) return;
            event.preventDefault();
            const offset = this.offsetHeight + this.stickyHeaderHeight();
            const top = target.getBoundingClientRect().top + window.scrollY - offset;
            window.scrollTo({ top, behavior: 'smooth' });
          });
        });

        this.targets = this.links
          .map((link) => document.getElementById(link.getAttribute('href').slice(1)))
          .filter(Boolean);

        if (this.targets.length === 0 || !('IntersectionObserver' in window)) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              this.links.forEach((link) => {
                const active = link.getAttribute('href').slice(1) === entry.target.id;
                link.classList.toggle('is-active', active);
              });
            });
          },
          { rootMargin: '-30% 0px -60% 0px' }
        );

        this.targets.forEach((target) => this.observer.observe(target));
      }

      stickyHeaderHeight() {
        const header = document.querySelector('sticky-header');
        return header ? header.offsetHeight : 0;
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }
    }
  );
}

if (!customElements.get('viyo-card')) {
  customElements.define(
    'viyo-card',
    class ViyoCard extends HTMLElement {
      connectedCallback() {
        this.pills = Array.from(this.querySelectorAll('[data-viyo-pill]'));
        if (this.pills.length === 0) return;

        this.priceEl = this.querySelector('[data-viyo-price]');
        this.compareEl = this.querySelector('[data-viyo-compare]');
        this.saveEl = this.querySelector('[data-viyo-save]');
        this.variantInput = this.querySelector('[data-viyo-variant-input]');
        this.submit = this.querySelector('[type="submit"]');

        this.pills.forEach((pill) => {
          pill.addEventListener('change', () => this.select(pill));
        });

        const checked = this.pills.find((pill) => pill.checked);
        if (checked) this.select(checked);
      }

      select(pill) {
        const { price, compare, save, variantId, available } = pill.dataset;

        if (this.priceEl) this.priceEl.textContent = price;

        // Compare-at and the saved chip only appear when there is a real discount.
        if (this.compareEl) {
          this.compareEl.textContent = compare || '';
          this.compareEl.hidden = !compare;
        }
        if (this.saveEl) {
          this.saveEl.textContent = save ? `${save}% OFF` : '';
          this.saveEl.hidden = !save;
        }

        if (this.variantInput) this.variantInput.value = variantId;

        if (this.submit) {
          const soldOut = available === 'false';
          this.submit.disabled = soldOut;
          this.submit.textContent = soldOut ? 'Sold out' : 'Add to cart';
        }
      }
    }
  );
}

if (!customElements.get('viyo-accordion')) {
  customElements.define(
    'viyo-accordion',
    class ViyoAccordion extends HTMLElement {
      connectedCallback() {
        if (!this.hasAttribute('data-single-open')) return;

        this.items = Array.from(this.querySelectorAll('details'));

        // Native <details> handles open/close; this only closes the siblings.
        this.items.forEach((item) => {
          item.addEventListener('toggle', () => {
            if (!item.open) return;
            this.items.forEach((other) => {
              if (other !== item) other.open = false;
            });
          });
        });
      }
    }
  );
}

/*
  Hero watermark parallax. Deliberately tiny (max ~60px of travel) and skipped
  entirely for reduced-motion users.
*/
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduced.matches) return;

  const targets = document.querySelectorAll('[data-viyo-parallax]');
  if (targets.length === 0) return;

  let ticking = false;

  const update = () => {
    targets.forEach((el) => {
      const rect = el.parentElement.getBoundingClientRect();
      const progress = Math.max(-1, Math.min(1, rect.top / window.innerHeight));
      el.style.transform = `translate3d(-50%, ${progress * 60}px, 0)`;
    });
    ticking = false;
  };

  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    },
    { passive: true }
  );

  update();
})();

/*
  Staggered fade-up. Dawn's own scroll-trigger handles section-level reveals;
  this adds the per-child 80ms cascade the brand look calls for.
*/
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduced.matches || !('IntersectionObserver' in window)) return;

  const groups = document.querySelectorAll('[data-viyo-stagger]');
  if (groups.length === 0) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        Array.from(entry.target.children).forEach((child, index) => {
          child.style.transitionDelay = `${index * 80}ms`;
          child.classList.add('viyo-revealed');
        });
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.15 }
  );

  groups.forEach((group) => {
    Array.from(group.children).forEach((child) => child.classList.add('viyo-reveal'));
    observer.observe(group);
  });
})();

if (!customElements.get('viyo-lab-search')) {
  customElements.define(
    'viyo-lab-search',
    class ViyoLabSearch extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        this.rows = Array.from(document.querySelectorAll('[data-viyo-lab-batch]'));
        this.empty = document.querySelector('[data-viyo-lab-empty]');

        if (!this.input) return;

        this.input.addEventListener('input', this.filter.bind(this));
        this.addEventListener('submit', (event) => event.preventDefault());
      }

      filter() {
        const query = this.input.value.trim().toLowerCase();
        let visible = 0;

        this.rows.forEach((row) => {
          const batch = (row.dataset.viyoLabBatch || '').toLowerCase();
          const match = query === '' || batch.includes(query);
          row.hidden = !match;
          if (match) visible += 1;
        });

        if (this.empty) this.empty.hidden = visible !== 0;
      }
    }
  );
}

if (!customElements.get('viyo-reels-slider')) {
  customElements.define(
    'viyo-reels-slider',
    class ViyoReelsSlider extends HTMLElement {
      connectedCallback() {
        this.slider = this.querySelector('.viyo-reels');
        this.reels = Array.from(this.querySelectorAll('.viyo-reel'));
        this.prevBtn = this.querySelector('.viyo-reels__nav-btn--prev');
        this.nextBtn = this.querySelector('.viyo-reels__nav-btn--next');
        
        if (!this.slider) return;

        this.prevBtn?.addEventListener('click', () => this.scrollBy(-1));
        this.nextBtn?.addEventListener('click', () => this.scrollBy(1));
        
        this.slider.addEventListener('scroll', this.updateActive.bind(this), { passive: true });
        
        // Initial setup
        setTimeout(() => this.updateActive(), 100);
      }

      scrollBy(direction) {
        if (!this.reels.length) return;
        const scrollAmount = this.reels[0].offsetWidth + parseInt(window.getComputedStyle(this.slider).gap || 0);
        this.slider.scrollBy({ left: scrollAmount * direction, behavior: 'smooth' });
      }

      updateActive() {
        if (!this.reels.length) return;
        
        const sliderCenter = this.slider.getBoundingClientRect().left + this.slider.offsetWidth / 2;
        let closest = null;
        let minDistance = Infinity;

        this.reels.forEach((reel) => {
          const rect = reel.getBoundingClientRect();
          const reelCenter = rect.left + rect.width / 2;
          const distance = Math.abs(sliderCenter - reelCenter);

          if (distance < minDistance) {
            minDistance = distance;
            closest = reel;
          }
        });

        this.reels.forEach((reel) => {
          reel.classList.toggle('is-active', reel === closest);
        });
      }
    }
  );
}
